import BuildIcon from '@mui/icons-material/Build';
import { Avatar, Box, Typography } from '@mui/material';
import React from 'react';
import { cardThemeStyles as styles } from 'features/inventory/equipments/components/tabs-component/log-book/cards/cardStyle';

interface TypeCellProps {
  /** The specific type slug identifier returned from backend row payloads */
  typeSlug: string | null | undefined;
  /** The fallback native description label if a type slug isn't found in the registry */
  fallbackLabel?: string | null | undefined;
  /** The active registry configuration object containing style properties */
  registry: Record<string, any>;
}

/**
 * Highly reusable and fully decoupled log cell component.
 * Free of inline switch evaluations to avoid memory leak rendering cycles.
 */
export const TypeCell = React.memo(({ typeSlug, fallbackLabel, registry }: TypeCellProps) => {
  // Resolve registry match, or safely assign fallback metrics on the fly
  const activeConfig = typeSlug ? registry[typeSlug] : undefined;

  const label = activeConfig?.label || fallbackLabel || 'EVENT';
  const Icon = activeConfig?.IconComponent || BuildIcon;
  const backgroundColor = activeConfig?.bgColor || '#f5f5f5';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {/* Normalized circular icon container element */}
      <Avatar
        sx={{
          width: 36,
          height: 36,
          backgroundColor: backgroundColor,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <Box sx={styles.iconCircle}>
          <Icon sx={{ fontSize: '1.25rem' }} />
        </Box>
      </Avatar>

      {/* Structured typography layout label string node */}
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: '0.9rem',
          letterSpacing: '0.03em',
          color: '#132f4c' // High contrast professional corporate theme color
        }}>
        {label}
      </Typography>
    </Box>
  );
});

TypeCell.displayName = 'LogBookTypeCell';
